<template>
	<div class="open-file-browser">
		<div v-if="$slots.default" class="browse-pre-header">
			<slot></slot>
		</div>
		<div v-if="error" class="errors">
			<span class="perr">{{ errorMessage }}</span>
		</div>

		<div class="browse-header">
			<sym-icon v-if="loading" name="refresh" class="uploadingIcon upload-or-volume"></sym-icon>
			<icon-button v-else-if='activeVolume' :name="activeVolume.icon" @click.prevent="closeFolder(ROOT)" class="upload-or-volume"></icon-button>

			<select v-model="volumeRef" tabindex='10' @change='reload'>
				<option v-for="volumeItem in volumes" :key="volumeItem.id" :value="volumeItem.id">{{ volumeItem.title }}</option>
			</select>

			<span class="path-to-volume">
				<span v-for="(pathItem, index) in path">
					<span class="path-separator">/</span>
					<span v-if="index===path.length-1">{{ pathItem.name }}</span>
					<a v-else href="#" @click.prevent="closeFolder(pathItem)">{{ pathItem.name }}</a>
				</span>
			</span>
		</div>
		<div class="browse-body" :style="gridStyle" tabindex="10" @keydown="keyDown">
			<div class="browse-body-header">
				<open-file-header v-for="column in headerColumns" v-bind="column" v-model="header[column.id].filter" :sortColumn="sortColumn" :sortOrderAsc="sortOrderAsc" @sortBy="sortBy" @reload="reload"/>
			</div>
			<div class="browse-body-rows">
				<div v-if="path.length>0" class="browse-row" :class="{'selected-row' : parentFolderSelected}" @click="rowClick($event, null)" @dblclick="rowDoubleClick($event, null)">
					<span class="centered"><sym-icon name="folder"/></span>
					<span class="folder">..</span>
					<span></span>
					<span></span>
				</div>
				<div v-if="isEmpty" class="browse-row">
					<span></span>
					<span class="no-data">{{ env.msg("volume.folder.emptyData") }}</span>
					<span></span>
					<span></span>
				</div>
				<template v-else>
					<div v-for="row in preparedRows" :key="row.id" :class="{'selected-row' : row.selected}" class="browse-row" @click="rowClick($event, row)" @dblclick="rowDoubleClick($event, row)">
						<template v-if="row.folder">
							<span class="centered"><sym-icon :name="row.icon"/></span>
							<span class="folder">{{ row.name }}</span>
						</template>
						<template v-else>
							<template v-if="preview">
								<span v-if="row.preview" class="image-preview-container centered"><img :src="previewPath(row.path)" :alt="row.name" class="image-preview"/></span>
								<span v-else class="centered"><sym-icon :name="row.icon"/></span>
								<span>{{ row.name }}</span>
							</template>
							<template v-else>
								<span class="centered"><sym-icon :name="row.icon"/></span>
								<span>{{ row.name }}</span>
							</template>
						</template>
						<span :title="formatDateTime(row.modified)" class="hideItemModify item">{{ formatDate(row.modified) }}</span>
						<span class="file-size-col">{{ formatSize(row.size) }}</span>
					</div>
				</template>

			</div>
		</div>

		<div class="browse-buttons">
			<button class="sbutton" @click="doCancel">{{ env.msg("bas.cancel") }}</button>
			<button class="button" :disabled="selectedItems.length===0 && !parentFolderSelected" @click="doAction">
				<template v-if="parentFolderSelected">{{ env.msg('content.parentFolder') }}</template>
				<template v-else-if="folderSelected">{{ env.msg('bas.open') }}</template>
				<template v-else><sym-icon v-if="openButtonIcon" :name="openButtonIcon"/> {{ openButtonLabel ?? env.msg("bas.open") }}</template>
			</button>
		</div>
	</div>
</template>
<style>
.open-file-browser {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
	max-height: 100%;

	& .errors {
		margin-bottom: var(--form-v-start-padding);
	}
}
.upload-or-volume.fi {
	width: 24px;
	text-align: left;

	&.uploadingIcon {
		text-align: center;
	}
}

.browse-header {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	flex: 0 0;
}

.browse-body {
	display: grid;
	flex: 1 1;
	overflow-y: auto;

	& > .browse-body-header {
		display: contents;
	}
	& > .browse-body-rows {
		display: grid;
		grid-column: 1 / -1;
		grid-template-columns: subgrid;

		max-height: 500px;
		overflow: auto;
	}
	&:focus-visible, &:focus {
		outline: none;
	}
}

.browse-buttons {
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	column-gap: 8px;
	align-items: baseline;
	-webkit-user-select: none;
	user-select: none;
	padding-top: var(--view-header-v-padding);
	padding-bottom: calc(var(--view-header-v-padding) - var(--form-v-padding));
	flex: 0 0;
}

.path-to-volume {
	font-size: var(--font-size-s);
	margin-left: 4px;
	--link-decoration: none;
}

.browse-row {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: center;

	cursor: pointer;
	padding-block: 4px;
	border-radius: var(--button-radius);
	user-select: none;
	-webkit-user-select: none;
	transition: background-color 250ms;

	&:nth-child(odd) {
		background-color: var(--agenda-bg-odd);
	}

	&:nth-child(even) {
		background-color: var(--agenda-bg-even);
	}

	&:hover {
		background-color: var(--agenda-bg-hover);
	}

	& > span {
		margin-inline: var(--agenda-h-padding);
		box-sizing: border-box;

		&.centered {
			justify-self: center;
		}
	}

	&.selected-row {
		color: var(--selection-fg);
		background-color: var(--selection-bg);
	}
}

.file-size-col {
	justify-self: right;
}
.no-data {
	font-weight: 500;
	opacity: 0.5;
}


.image-preview-container {
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100px;
	height: 75px;
	overflow: hidden;
}
.image-preview {
	max-width: 100px;
	max-height: 75px;
	border-radius: 3px;
}




.browse-container {
	display: grid;
	padding: 0;
	grid-template-columns: min-content 1fr min-content min-content;
	grid-auto-rows: auto;
	grid-template-rows: auto;
	grid-template-areas:
		" header    header info   info "
		" preview   name   modify   size ";
}

.hidePreview {
	grid-template-columns: 1fr max-content max-content;
	grid-template-areas:
		" header info   info "
		" name   modify   size ";
}

.browse-tail {
	max-height: 500px;
	overflow-y: auto;
	padding-bottom: 10px;
}

@media only screen and (max-width: 600px) {
	.browse-container {
		grid-template-columns: 1fr 110px 90px;
		grid-template-areas:
			" header info   info "
			" name   modify   size "
	}

	.browse-preview {
		display: none;
	}

}

@media only screen and (max-width: 450px) {
	.browse-container {
		grid-template-columns: 1fr 90px;
		grid-template-areas:
			" header info "
			" name   size "
	}

	.browse-preview {
		display: none;
	}

	.browse-modify {
		display: none;
	}

	.hideItemModify {
		display: none;
	}
}

.browse-container .row {
	display: contents;
}

.browse-container .row:nth-child(even) * {
	background-color: var(--agenda-bg-odd);
}

.row > div {
	padding-top: 2px;
	padding-bottom: 2px;
}


.row:hover > div {
	background-color: var(--agenda-bg-hover) !important;
}

.browse-container .head {
	padding-top: 10px;
	grid-area: header;
}

.browse-container .head-info {
	padding-top: 10px;
	grid-area: info;
	justify-self: end;
}

.hidePreview .browse-preview {
	display: none;
}


.browse-container > div {
}

.folder {
	font-weight: bold;
	cursor: pointer;
}

.loading-box {
	color: var(--agenda-head-fg-hover);
	opacity: 0.5;
	display: inline-block;
	width: 28px;
}
</style>

<script setup lang="ts">
import {ref, computed, onMounted, watch, type CSSProperties} from 'vue';
import SymIcon from "@fd/helpers/SymIcon.vue";
import type {FolderStructure, FolderStructureData, Volume} from "./FolderStructure";
import {formatSize, formatDate, formatDateTime} from "./FolderStructure";
import {injectEnv} from "@fd/Environment";
import OpenFileHeader from "./OpenFileHeader.vue";
import IconButton from "@fd/helpers/IconButton.vue";

const props = defineProps({
	volume: {type: String, required: false, default: "attFile"},
	filter: String,
	preview: Boolean,
	disableClientStorage: Boolean,
	/** Enable selection of multiple files */
	multi: Boolean,
	openButtonLabel: String,
	openButtonIcon: String
});

interface PathItem {
	path: string;
	name: string;
}

const VOLUMES_REST_API = "api/volumes";
const ROOT = <PathItem>{path: "", name: "/"};

const emit = defineEmits(['selected', 'cancel']);
const env = injectEnv();

const volumeRef = ref(props.volume);
const volumes = ref<Volumes[]>([]);
const path = ref<PathItem[]>([]);
const loading = ref(true);
const storageKey: string = "CRM.FileBrowser";
const sortColumn = ref("");
const sortOrderAsc = ref(true);
const error = ref(null);
const data = ref<FolderStructureData>({
	total: 0,
	position: 0,
	size: 0,
	items: []
});
const preview = ref<boolean>(props.preview);

const header = ref({
	"preview": {
		id: "preview",
		title: "" /*env.msg("picture.preview")*/,
		isSortable: false,
		isFilterable: false,
		class: "browse-preview",
		filter: ""
	},
	"name": {
		id: "name",
		title: env.msg("file.name"),
		isSortable: true,
		isFilterable: true,
		class: "browse-name",
		filter: ""
	},
	"modified": {
		id: "modified",
		title: env.msg("th.modified"),
		isSortable: true,
		isFilterable: false,
		class: "browse-modify",
		filter: ""
	},
	"size": {
		id: "size",
		title: env.msg("file.size"),
		isSortable: true,
		isFilterable: false,
		class: "browse-size",
		filter: ""
	}
});

const headerColumns = computed(() => {
	let columns = [];
	const keys = Object.keys(header.value);
	keys.forEach(function (key) {
		columns.push(header.value[key]);
	});
	return columns;
});

const gridStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	const template:string[] = [];
	for (let col of headerColumns.value) {
		template.push(col.id === "name" ? "1fr" : "max-content");
	}
	s.gridTemplateColumns = template.join(" ");
	return s;
});

const preparedRows = computed<FolderStructure[]>(() => {
	let newRows = data.value.items ? data.value.items : [];

	if (sortColumn.value) { //sorting if sort column is choosen
		newRows = newRows.slice().sort(function (first, second) {
			if (first.folder != second.folder) {//check if folder mode
				//return (first.folder ? 1 : -1) * sortOrderAsc.value;
				return first.folder ? -1 : 1;
			}

			let firstValue = first[sortColumn.value];
			let secondValue = second[sortColumn.value];

			if (firstValue === secondValue) {
				return 0;
			} else {
				return firstValue.localeCompare(secondValue) * (sortOrderAsc.value ? 1 : -1);
			}
		})
	}
	return newRows;
});

let lastClick = 0;

function rowClick(e:MouseEvent, item:FolderStructure|null) {
	const clickDiff = e.timeStamp - lastClick;
	lastClick = e.timeStamp;
	if (clickDiff < 300) return;	// handled in double click
	toggleSelected(item);
}

function rowDoubleClick(e:MouseEvent, item:FolderStructure|null) {
	if (item) {
		if (item.folder) openFolder(item);
		else emit('selected', selectedItems.value);
	} else {
		closeFolder();
		parentFolderSelected.value = false;
	}
}

function doAction() {
	if (parentFolderSelected.value) closeFolder();
	else if (folderSelected.value) openFolder(selectedItems.value[0]);
	else if (selectedItems.value.length > 0) emit('selected',selectedItems.value);
}

function doCancel() {
	emit('cancel');
}

function dataLength():number {
	if (data.value.items) return data.value.items.length;
	return 0;
}

function selectIndex(idx: number) {
	if (data.value.items) {
		if (idx >= 0 && idx < data.value.items.length) {
			data.value.items[idx].selected = true;
		}
	}
}

function navigateUp() {
	const first = firstSelectedIndex.value;
	if (first === 0 && path.value.length > 0) {
		unselectAll();
		parentFolderSelected.value = true;
	} else if (first > 0) {
		unselectAll();
		selectIndex(first - 1);
	} else {
		const l = dataLength();
		if (l > 0) {
			unselectAll();
			selectIndex(l - 1);
		} else if (path.value.length > 0) {
			unselectAll();
			parentFolderSelected.value = true;
		}
	}
}

function navigateDown() {
	const last = lastSelectedIndex.value;
	const l = dataLength();

	if (last < 0 && path.value.length > 0 && !parentFolderSelected.value) {
		unselectAll();
		parentFolderSelected.value = true;
	} else if (last < (l - 1)) {
		unselectAll();
		selectIndex(last + 1);
	} else {
		if (l > 0) {
			unselectAll();
			if (path.value.length > 0) parentFolderSelected.value = true;
			else selectIndex(0);
		} else if (path.value.length > 0) {
			unselectAll();
			parentFolderSelected.value = true;
		}
	}
}

function keyDown(e:KeyboardEvent):void {
	if (e.key === "Enter" && (selectedItems.value.length>0 || parentFolderSelected.value)) {
		doAction();
		e.preventDefault();
		return;
	}
	// Navigation
	if (e.key === "ArrowUp") {
		navigateUp();
		e.preventDefault();
		return;
	}
	if (e.key === "ArrowDown") {
		navigateDown();
		e.preventDefault();
		return;
	}
}

const activeVolume = computed<Volume>(() => {
	if (volumeRef.value && volumes.value) {
		return volumes.value.find(item => item.id === volumeRef.value);
	}
	return null;
});

const endpoint = computed<string>(() => {
	if (activeVolume.value) {
		return VOLUMES_REST_API + activeVolume.value.path;
	}
	return null;
});

const isEmpty = computed<boolean>(() => {
	return !data.value.items;
});

const errorMessage = computed<boolean>(() => {
	return error.value ? error.value : null; //TODO
});

const selectedItems = computed<FolderStructure[]>(() => {
	if (data.value.items) {
		return data.value.items.filter(item => item.selected);
	} else {
		return [];
	}
});

const firstSelectedIndex = computed<number>(() => {
	if (data.value.items) {
		for (let i = 0; i < data.value.items.length; i++) {
			const item = data.value.items[i];
			if (item.selected) return i;
		}
	}
	return -1;
});

const lastSelectedIndex = computed<number>(() => {
	if (data.value.items) {
		for (let i = data.value.items.length - 1; i >= 0; i--) {
			const item = data.value.items[i];
			if (item.selected) return i;
		}
	}
	return -1;
});

function previewPath(path: string): string {
	//return "https://interactive-examples.mdn.mozilla.net/media/cc0-images/grapefruit-slice-332-332.jpg";
	return endpoint.value + path + "/preview";
}

function openFolder(folder: FolderStructure): void {
	const pathItem: PathItem = <PathItem>{path: folder.path, name: folder.name};
	path.value.push(pathItem);
	reload();
}

function closeFolder(folder?: PathItem): void {
	if (folder) {
		if (folder.name === "/") {
			path.value = [];
		} else {
			const index = path.value.findIndex(item => item.path === folder.path);
			if (index >= 0) {
				path.value = path.value.slice(0, index + 1);
			}
		}
	} else {
		path.value.pop();
	}
	reload();
}

function reload(): void {
	const item: PathItem = path.value.length > 0 ? path.value.slice(-1)[0] : ROOT;
	load(openFolderRestApi(item));
}

function openFolderRestApi(pathItem: PathItem): string {
	let url = endpoint.value + pathItem.path;
	//add filter
	if (props.filter) {
		url = env.net.urlParam(url, "mimeType", props.filter);
	}
	if (header.value["name"].filter) {
		url = env.net.urlParam(url, "name", header.value["name"].filter);
	}
	return url;
}

function load(url?: string): void {
	let finalUrl = url ? url : endpoint.value;
	loading.value = true;
	loadData(finalUrl).then(() => {
		loading.value = false;
	});
}

function loadData(url: string): Promise<boolean> {
	resetSelected();
	console.log("loading endpoint url:" + url);
	return env.net.fetch(url)
		.then(response => response.json())
		.then(jsonData => {
			//console.log("Loaded items:" + JSON.stringify(jsonData.items));
			data.value = jsonData;
			return true;
		})
		.catch((error) => {
			console.log("error loading data:" + error);
			error.value = error;
			data.value.items = [];
			return false;
		});
}

function resetSelected(): void {
	if (data.value.items) {
		data.value.items.forEach(item => item.selected = false);
	}
}

function initVolumes(): Promise<boolean> {
	console.log("loading volumes");
	return env.net.fetch(VOLUMES_REST_API)
		.then(response => response.json())
		.then(jsonData => {
			//console.log("Loaded volumes:"+JSON.stringify(jsonData));
			volumes.value = jsonData;
			return true;
		})
		.catch((error) => {
			console.log("error loading volumes:" + error);
			error.value = error;
			return false;
		});
}

function unselectAll() {
	parentFolderSelected.value = false;
	if (Array.isArray(data.value.items)) {
		for (let item of data.value.items) {
			item.selected = false;
		}
	}
}

const folderSelected = computed<boolean>(() => {
	const si = selectedItems.value;
	return si.length === 1 && si[0].folder;
});

/** Selected row with ".." representing parent folder */
const parentFolderSelected = ref<boolean>(false);

function toggleSelected(row: FolderStructure|null): void {
	if (!props.multi) unselectAll();
	if (row) {
		row.selected = !row.selected;
	} else {
		parentFolderSelected.value = !parentFolderSelected.value;
	}
}

function sendSelected(row: FolderStructure): void {
	emit('selected', [row]);
}

function saveToSession(newPath: PathItem[]): void {
	console.log("saving path:" + JSON.stringify(newPath));
	sessionStorage.setItem(storageKey, JSON.stringify(newPath));
}

watch(path, (newPath: PathItem[]) => {
		saveToSession(newPath);
	},
	{deep: !props.disableClientStorage}
);

watch(volumeRef, (newVolumeRef: String) => {
		path.value = [];
	}
);

function sortBy(column: string): void {
	if (sortColumn.value === column) {
		sortOrderAsc.value = !sortOrderAsc.value;
	} else {
		sortColumn.value = column;
		sortOrderAsc.value = true;
	}
}

//on create
initVolumes().then(() => {
	if (!props.disableClientStorage) {
		let storedPath: string = sessionStorage.getItem(storageKey);
		if (storedPath != null) {
			console.log("restored path from sessionStorage:" + storedPath);
			let newPath = JSON.parse(storedPath);
			const item: PathItem = newPath.length > 0 ? newPath.slice(-1)[0] : ROOT;
			path.value = newPath;
			load(openFolderRestApi(item));
			return;
		}
	}
	console.log("Init OpenFileBrowser on base endpoint");
	load();
});
</script>